import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardHeader,
	CardWrapper,
	CtaButton,
	DeleteConfirmModal,
	EmptyTable,
	Header,
	NameDialog,
	ProductSettingsTabs,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useSearch, useTableSelect } from 'shared/hooks'
import type { GenderItem } from 'features/genders/types/genders.types'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useFetchGenders } from './queries/useFetchGenders'
import { useCreateGenderMutation } from './mutations/useCreateGenderMutation'
import { useEditGenderMutation } from './mutations/useEditGenderMutation'
import { useDeleteGendersMutation } from './mutations/useDeleteGendersMutation'
import { useDeleteGenderMutation } from './mutations/useDeleteGenderMutation'
import { TableSection } from './organisms/TableSection'

const GendersPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleSearch } = useSearch({ name: 'search' })
	const [checkedGenders, setCheckedGenders] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<string | null>(null)
	const [editedGender, setEditedGender] = useState<GenderItem | null>(null)
	const { genders, isGendersLoading } = useFetchGenders()
	const { select } = useTableSelect({ nodes: genders?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const [actionsFilter] = useActionFilters(!checkedGenders.length)

	const {
		isOpen: isAddDialogOpen,
		openModal: openAddDialog,
		closeModal: closeAddDialog
	} = useModal()
	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()

	const { createGender, createGenderLoading } = useCreateGenderMutation({
		onSuccess: closeAddDialog
	})

	const { editGenderMutation, isEditGenderLoading } = useEditGenderMutation({
		onSuccess: () => setEditedGender(null)
	})

	const { deleteGenderMutation } = useDeleteGenderMutation({
		id: idToBeRemoved,
		onSettled: closeDeleteModal
	})

	const { deleteGendersMutation } = useDeleteGendersMutation({
		ids: checkedGenders,
		onSettled: () => {
			select.fns.onRemoveAll()
			closeBulkDeleteModal()
		}
	})

	function onSelectChange(action: any, state: any) {
		setCheckedGenders(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id.toString())
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onEdit: setEditedGender, onDelete: handleDelete })

	const handleAction = (action: string) => {
		if (action === 'delete') openBulkDeleteModal()
	}

	const renderContent = () => {
		if (isGendersLoading) return <EmptyTable isLoading />
		if (!genders?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No genders yet')} />

		return <TableSection genders={genders} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Products')}
				tabs={<ProductSettingsTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Genders')}
						description={t("Gender grouping shown in the store's filters.")}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search genders')}
							placeholder={t('Search genders')}
							onChange={handleSearch}
						/>
						<CtaButton text={t('Add gender')} onClick={openAddDialog} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isAddDialogOpen && (
				<NameDialog
					title={t('Add gender')}
					submitText={t('Create')}
					isLoading={createGenderLoading}
					closeModal={closeAddDialog}
					onSubmit={createGender}
				/>
			)}
			{editedGender && (
				<NameDialog
					title={t('Edit gender')}
					submitText={t('Save')}
					defaultValue={editedGender.name}
					isLoading={isEditGenderLoading}
					closeModal={() => setEditedGender(null)}
					onSubmit={(data) => editGenderMutation({ id: editedGender.id, data })}
				/>
			)}
			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete gender')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={deleteGenderMutation}>
					<p>{t('You are about to remove a gender. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected genders')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={deleteGendersMutation}>
					<p>
						{t('You are about to remove {{ count }} genders. Are you sure you want to proceed?', {
							count: checkedGenders.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
		</>
	)
}

export default GendersPage
